import { useLocalStorageSWR } from "@/hooks/swr/localStorage";
import { now, TIME_SECONDS } from "@/utils/dateTimeUtils";
import { SubscriptionType } from "@knowt/syncing/graphql/schema";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useEffect, useRef } from "react";

const TIMES_THE_WELCOME_OFFER_SHOULD_BE_SHOWN = 3;

const useWelcomeOffer = ({
    onShowOffer,
}: {
    onShowOffer: (offerNumber: number) => void;
}) => {
    const hasChecked = useRef(false);
    const { loginInProgress, subscriptionType, areSubsAllowed } = useCurrentUser({ waitForFallbackData: true });

    const { data: welcomeOfferTimesViewed, set: setWelcomeOfferTimesViewed } = useLocalStorageSWR(
        "welcome-offer-times-viewed",
        undefined,
        {
            default: 0,
        }
    );
    const { data: lastShownOffer, set: setLastShownOffer } = useLocalStorageSWR("welcome-offer-last-shown", undefined, {
        default: 0,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        const showWelcomeOffer = async () => {
            if (
                subscriptionType === SubscriptionType.LIMITLESS ||
                subscriptionType === SubscriptionType.TEACHER_LIMITLESS ||
                !areSubsAllowed ||
                loginInProgress ||
                hasChecked.current
            ) {
                return;
            }

            hasChecked.current = true;

            if (lastShownOffer) {
                const diff = now() - lastShownOffer;
                if (diff < 12 * TIME_SECONDS.HOUR) {
                    return;
                }
            }

            if (typeof welcomeOfferTimesViewed !== "number") {
                // We don't show in the first time, because the user already saw the offer in the initial setup
                setWelcomeOfferTimesViewed(1);
            } else if (welcomeOfferTimesViewed <= TIMES_THE_WELCOME_OFFER_SHOULD_BE_SHOWN) {
                setWelcomeOfferTimesViewed(welcomeOfferTimesViewed + 1);
                onShowOffer(welcomeOfferTimesViewed + 1);
            }
            setLastShownOffer(now());
        };

        showWelcomeOffer();
    }, [loginInProgress]);
};

export default useWelcomeOffer;
